import { Component } from '@theme/component';
import {
  catalogue,
  estimate,
  invalidateCatalogue,
  modelYears,
  PRIVATE_ORIGIN,
  QuoteError,
} from '@theme/trade-in-valuation';

/**
 * Marks the options this component generated, so a refill can replace them
 * without touching the placeholder option Liquid rendered.
 */
const GENERATED = 'data-generated';

/** Applied to whatever the criteria link scrolls to, for scroll-margin. */
const SCROLL_TARGET = 'trade-in-calculator-scroll-target';

/**
 * The Liquid-rendered text of each select's placeholder option, captured
 * before anything overwrites it so it can be put back.
 *
 * @type {WeakMap<HTMLOptionElement, string>}
 */
const originalPlaceholders = new WeakMap();

/**
 * The trade-in price calculator (SD-383).
 *
 * Collects the five inputs, asks the trade API to price them and renders the
 * result. Everything about the API - endpoints, the display rule for net vs
 * gross, error codes, catalogue caching - lives in `trade-in-valuation.js`;
 * this component does form handling and presentation only.
 *
 * The brand and category selects are filled from the API rather than from
 * Liquid, because the guide is explicit that its lists are the allowlist and a
 * hand-written dropdown will drift out of it. The calculator is useless
 * without JS anyway, so there is nothing to degrade to.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLButtonElement} submit
 * @property {HTMLSelectElement} brand
 * @property {HTMLSelectElement} modelYear
 * @property {HTMLSelectElement} category
 * @property {HTMLInputElement} mileage
 * @property {HTMLInputElement} retailPrice
 * @property {HTMLElement} result
 * @property {HTMLElement} estimateMin
 * @property {HTMLElement} estimateMax
 * @property {HTMLElement} rangeUpper
 * @property {HTMLElement} [brandError]
 * @property {HTMLElement} [modelYearError]
 * @property {HTMLElement} [categoryError]
 * @property {HTMLElement} [mileageError]
 * @property {HTMLElement} [retailPriceError]
 * @property {HTMLElement} [formError]
 * @property {HTMLElement} [staleError]
 * @property {HTMLAnchorElement} [criteria]
 *
 * @extends {Component<Refs>}
 */
class TradeInCalculatorComponent extends Component {
  requiredRefs = [
    'form',
    'submit',
    'brand',
    'modelYear',
    'category',
    'mileage',
    'retailPrice',
    'result',
    'estimateMin',
    'estimateMax',
    'rangeUpper',
  ];

  /** False until the API's brand and category lists are in the selects. */
  #ready = false;

  connectedCallback() {
    super.connectedCallback();
    this.#loadOptions();
    this.#syncCriteriaLink();
  }

  updatedCallback() {
    super.updatedCallback();
    // A theme-editor re-render hands back selects holding only the
    // Liquid-rendered placeholder option. The catalogue is cached, so this
    // normally refills without a request.
    this.#loadOptions();
    // The merchant may have just edited the link, or the block it points at.
    this.#syncCriteriaLink();
  }

  /** @returns {string} */
  get #baseUrl() {
    return (this.dataset.apiBase || '').replace(/\/+$/, '');
  }

  /**
   * Half-width of the quoted range, as a fraction. Zero means show the single
   * price the API returned, which is the default and the honest option.
   *
   * @returns {number}
   */
  get #rangeSpread() {
    const percent = Number(this.dataset.rangeSpread);
    return Number.isFinite(percent) && percent > 0 ? percent / 100 : 0;
  }

  /**
   * Oldest model year to offer. `modelYears` rejects an implausible value on
   * its own, so a blank or mistyped block setting falls back to its default.
   *
   * @returns {number}
   */
  get #oldestModelYear() {
    return Number(this.dataset.oldestModelYear);
  }

  /**
   * Every field, paired with the element that shows its error.
   *
   * @returns {{ input: HTMLSelectElement | HTMLInputElement, error: HTMLElement | undefined }[]}
   */
  get #fields() {
    const { refs } = this;

    return [
      { input: refs.brand, error: refs.brandError },
      { input: refs.modelYear, error: refs.modelYearError },
      { input: refs.category, error: refs.categoryError },
      { input: refs.mileage, error: refs.mileageError },
      { input: refs.retailPrice, error: refs.retailPriceError },
    ];
  }

  /**
   * Validates, prices, renders.
   *
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    this.#clearFormErrors();

    const invalid = this.#validate();

    if (invalid.length > 0) {
      invalid[0]?.focus();
      return;
    }

    this.#busy = true;

    try {
      const { price, currency } = await estimate(
        {
          brand: this.refs.brand.value,
          category: this.refs.category.value,
          modelYear: Math.round(Number(this.refs.modelYear.value)),
          // The customer no longer picks an origin: every bike is quoted as a
          // private sale. Still sent explicitly, because it is a required
          // pricing input of the endpoint - see PRIVATE_ORIGIN.
          origin: PRIVATE_ORIGIN,
          // The API takes km as an integer and rejects a decimal outright.
          mileage: Math.round(Number(this.refs.mileage.value)),
          retailPrice: Number(this.refs.retailPrice.value),
        },
        this.#baseUrl
      );

      this.#showResult(price, currency);
    } catch (error) {
      console.error('[trade-in-calculator] quote failed', error);
      this.refs.result.setAttribute('hidden', '');

      // A rejected brand or category means our cached list has drifted from
      // the allowlist, so throw it away and refill from the API.
      if (error instanceof QuoteError && STALE_CODES.has(error.code)) {
        invalidateCatalogue();
        this.refs.staleError?.removeAttribute('hidden');
        this.#loadOptions();
      } else {
        this.refs.formError?.removeAttribute('hidden');
      }
    } finally {
      this.#busy = false;
    }
  };

  /**
   * Hides a result once any input changes, so a stale price can never sit
   * under inputs it was not calculated from.
   */
  handleInput = () => {
    this.refs.result.setAttribute('hidden', '');
    this.#clearFormErrors();
  };

  /**
   * Jumps to the acceptance criteria further down the page.
   *
   * An ordinary URL is left to the browser. A `#anchor` is handled here because
   * this theme has no per-block ID setting, so there is rarely an element with
   * the id the merchant typed - {@link #criteriaTarget} falls back to matching a
   * heading by text, which is how the link works without one.
   *
   * @param {MouseEvent} event
   */
  handleCriteriaClick = (event) => {
    const href = this.refs.criteria?.getAttribute('href') ?? '';
    if (!href.startsWith('#')) return;

    // Swallow the click either way: a bare "#" would otherwise jump to the top
    // of the page, which is the one thing worse than the link doing nothing.
    event.preventDefault();

    const target = this.#criteriaTarget;
    if (!target) return;

    // The criteria are often inside an accordion, which would leave the
    // customer looking at a collapsed row.
    const details = target.closest('details');
    if (details) details.open = true;

    target.classList.add(SCROLL_TARGET);

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });

    // Carry keyboard and screen-reader users along with the scroll.
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  };

  /**
   * The element the criteria link points at, or null if there is nothing to
   * jump to. Tries the literal id first, then the heading text.
   *
   * @returns {HTMLElement | null}
   */
  get #criteriaTarget() {
    const href = this.refs.criteria?.getAttribute('href') ?? '';

    if (href.length > 1 && href.startsWith('#')) {
      const raw = href.slice(1);
      // The href is typed by hand, so it may hold a stray "%" that
      // decodeURIComponent refuses; the literal id is the sensible fallback.
      let id = raw;
      try {
        id = decodeURIComponent(raw);
      } catch {
        // keep raw
      }

      const byId = document.getElementById(id);
      if (byId) return byId;
    }

    const needle = this.dataset.criteriaHeading?.trim().toLowerCase();
    if (!needle) return null;

    // A real heading first. `summary` counts, because an accordion's visible
    // title is its summary.
    const heading = this.#findByText('h1, h2, h3, h4, h5, h6, summary', needle);
    if (heading) return heading;

    // Then ordinary text. The theme's `text` block outputs whatever the
    // merchant typed into the rich-text field, and a "Heading 3" preset there
    // still renders as a <p> - which is exactly how "Annahmekriterien im
    // Überblick" exists on the trade-in page. Without this pass the link finds
    // nothing and hides itself.
    return this.#findByText('p, li, dt, figcaption, blockquote', needle, true);
  }

  /**
   * First element matching `selector` whose text contains `needle`.
   *
   * @param {string} selector
   * @param {string} needle - already lowercased
   * @param {boolean} [preferShortest] - pick the tersest match rather than the
   *   first, so a one-line title wins over a paragraph that merely mentions it
   * @returns {HTMLElement | null}
   */
  #findByText(selector, needle, preferShortest = false) {
    /** @type {HTMLElement | null} */
    let best = null;
    let bestLength = Infinity;

    for (const candidate of document.querySelectorAll(selector)) {
      // Skip the calculator itself - the link's own label contains the needle.
      if (this.contains(candidate)) continue;

      const text = candidate.textContent?.trim();
      if (!text || !text.toLowerCase().includes(needle)) continue;

      if (!preferShortest) return /** @type {HTMLElement} */ (candidate);

      if (text.length < bestLength) {
        best = /** @type {HTMLElement} */ (candidate);
        bestLength = text.length;
      }
    }

    return best;
  }

  /**
   * Hides the criteria link when it has nowhere to go, so customers never click
   * a link that does nothing. Kept visible in the theme editor, where the
   * merchant needs to see it in order to configure it.
   */
  #syncCriteriaLink() {
    const link = this.refs.criteria;
    if (!link) return;

    const href = link.getAttribute('href') ?? '';
    // A real URL is the browser's problem, not ours.
    if (!href.startsWith('#')) return;

    if (this.#criteriaTarget) {
      link.hidden = false;
      return;
    }

    if (window.Shopify?.designMode) {
      console.warn(
        `[trade-in-calculator] "${link.textContent?.trim()}" has nowhere to go: no element with id "${href.slice(
          1
        )}" and no heading or paragraph containing "${this.dataset.criteriaHeading ?? ''}" on this page. Set the block's acceptance-criteria heading text to match the title you want to jump to. Hidden on the live storefront.`
      );
      return;
    }

    link.hidden = true;
  }

  /**
   * @param {boolean} value
   */
  set #busy(value) {
    // Never re-enable submit while the catalogue is missing.
    this.refs.submit.disabled = value || !this.#ready;
    this.refs.form.setAttribute('aria-busy', String(value));
  }

  #clearFormErrors() {
    this.refs.formError?.setAttribute('hidden', '');
    this.refs.staleError?.setAttribute('hidden', '');
  }

  /**
   * Flags every empty or nonsensical field.
   *
   * @returns {(HTMLSelectElement | HTMLInputElement)[]} the invalid fields, in document order
   */
  #validate() {
    /** @type {(HTMLSelectElement | HTMLInputElement)[]} */
    const invalid = [];

    for (const { input, error } of this.#fields) {
      const value = input.value.trim();
      // Selects carry an empty-valued placeholder option; the number inputs
      // must be a real, non-negative number, and a price of 0 is not a price.
      const isNumber = input instanceof HTMLInputElement;
      const number = Number(value);
      const ok =
        value !== '' &&
        (!isNumber || (Number.isFinite(number) && number >= 0 && (input !== this.refs.retailPrice || number > 0)));

      if (ok) {
        input.removeAttribute('aria-invalid');
        error?.setAttribute('hidden', '');
      } else {
        input.setAttribute('aria-invalid', 'true');
        error?.removeAttribute('hidden');
        invalid.push(input);
      }
    }

    return invalid;
  }

  /**
   * @param {number} price - the figure the API says to show
   * @param {string} currency
   */
  #showResult(price, currency) {
    const spread = this.#rangeSpread;

    if (spread > 0) {
      // Presentation only. The API returns one number; widening it into a
      // range is a merchant choice, which is why the spread is a setting and
      // defaults to off.
      this.refs.estimateMin.textContent = this.#formatMoney(price * (1 - spread), currency);
      this.refs.estimateMax.textContent = this.#formatMoney(price * (1 + spread), currency);
      this.refs.rangeUpper.removeAttribute('hidden');
    } else {
      this.refs.estimateMin.textContent = this.#formatMoney(price, currency);
      this.refs.estimateMax.textContent = '';
      this.refs.rangeUpper.setAttribute('hidden', '');
    }

    this.refs.result.removeAttribute('hidden');

    // The result appears below the button, so on a short viewport the customer
    // may not see that anything happened.
    this.refs.result.focus();
  }

  /**
   * @param {number} value
   * @param {string} currency
   * @returns {string}
   */
  #formatMoney(value, currency) {
    const locale = document.documentElement.lang || 'de';
    const rounded = Math.round(value);

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(rounded);
    } catch {
      // An unexpected currency code should not take the whole result down.
      return `${rounded} ${currency}`;
    }
  }

  /**
   * Fills the three selects. Model years are local; brands and categories come
   * from the API, which is the allowlist for both.
   */
  async #loadOptions() {
    const years = modelYears(this.#oldestModelYear).map((year) => ({ value: String(year), label: String(year) }));

    fill(this.refs.modelYear, years);

    // Read before anything can overwrite them, so the pristine Liquid text is
    // what gets restored after a failed load.
    const remote = [placeholderOf(this.refs.brand), placeholderOf(this.refs.category)];

    this.refs.submit.disabled = true;

    try {
      const { brands, categories } = await catalogue(this.#baseUrl);

      fill(this.refs.brand, brands);
      fill(this.refs.category, categories);

      for (const option of remote) restorePlaceholder(option);

      this.#ready = true;
      this.refs.submit.disabled = false;
    } catch (error) {
      // Without the allowlist there is nothing valid the customer could pick,
      // so the form stays disabled rather than guaranteeing a 422.
      console.error('[trade-in-calculator] could not load brands and categories', error);

      // Say so in the two empty selects. A generic message under the button
      // does not explain why these specific dropdowns have nothing in them.
      const message = this.dataset.optionsUnavailable;
      if (message) {
        for (const option of remote) {
          if (option) option.textContent = message;
        }
      }

      this.#ready = false;
      this.refs.submit.disabled = true;
      this.refs.formError?.removeAttribute('hidden');
    }
  }
}

/**
 * Error codes that mean our cached brand/category list has drifted from the
 * API's allowlist, so refetching it and asking for a retry can actually help.
 *
 * `invalid_bike_origin` is deliberately not here. The origin is a constant now
 * (PRIVATE_ORIGIN), so the customer has nothing to correct and no catalogue
 * refetch could fix it - it means our string no longer matches the API's. That
 * falls through to the generic "unavailable" message instead.
 */
const STALE_CODES = new Set(['unknown_brand_or_category']);

/**
 * A select's placeholder option - the one Liquid rendered, as opposed to the
 * ones this file generates. Remembers its text the first time it is seen.
 *
 * @param {HTMLSelectElement} select
 * @returns {HTMLOptionElement | null}
 */
function placeholderOf(select) {
  const option = select.querySelector(`option:not([${GENERATED}])`);
  if (!option) return null;

  if (!originalPlaceholders.has(option)) originalPlaceholders.set(option, option.textContent ?? '');

  return option;
}

/**
 * @param {HTMLOptionElement | null} option
 */
function restorePlaceholder(option) {
  if (!option) return;

  const original = originalPlaceholders.get(option);
  if (original != null) option.textContent = original;
}

/**
 * Replaces a select's generated options, keeping the current selection if it
 * still exists.
 *
 * @param {HTMLSelectElement} select
 * @param {import('@theme/trade-in-valuation').Option[]} options
 */
function fill(select, options) {
  const selected = select.value;

  for (const option of select.querySelectorAll(`option[${GENERATED}]`)) option.remove();

  const fragment = document.createDocumentFragment();

  for (const { value, label } of options) {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = label;
    option.setAttribute(GENERATED, '');
    fragment.append(option);
  }

  select.append(fragment);

  if (selected) {
    select.value = selected;
    // A selection that is no longer on offer (the merchant raised the oldest
    // model year) leaves the select blank; show the placeholder instead, which
    // is an honest "nothing chosen" and what validation already sees.
    if (select.value !== selected) select.selectedIndex = 0;
  }
}

if (!customElements.get('trade-in-calculator-component')) {
  customElements.define('trade-in-calculator-component', TradeInCalculatorComponent);
}
